import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Eye, EyeOff, Lock, ChevronRight, Loader2, AlertCircle, KeyRound } from 'lucide-react';
import { usePasscode } from '../context/PasscodeContext';
import { authAPI } from '../services/api';

export default function PasscodeSetupPage() {
  const navigate = useNavigate();
  const {
    isPasscodeSet,
    isPasscodeEnabled,
    passcodeLength,
    setPasscode,
    togglePasscodeEnabled,
    verifyPasscode,
    clearPasscode
  } = usePasscode();

  const [mode, setMode] = useState('menu');
  const [verifyAction, setVerifyAction] = useState('disable');
  const [currentPasscode, setCurrentPasscode] = useState('');
  const [newPasscode, setNewPasscode] = useState('');
  const [confirmPasscode, setConfirmPasscode] = useState('');
  const [selectedLength, setSelectedLength] = useState(passcodeLength);
  const [error, setError] = useState('');
  const [showPasscode, setShowPasscode] = useState(false);
  const [password, setPassword] = useState('');
  const [verifyingPassword, setVerifyingPassword] = useState(false);
  const [shake, setShake] = useState(false);

  const savedNewPasscode = useRef('');
  const hiddenInputRef = useRef(null);

  const isPasscodeEntryMode = ['set', 'confirm', 'verify-current'].includes(mode);

  // Lock body scroll on mobile during passcode entry
  useEffect(() => {
    if (!isPasscodeEntryMode) return;
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    document.body.style.top = '0';
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
    };
  }, [isPasscodeEntryMode]);

  // Reset state when mode changes
  useEffect(() => {
    setCurrentPasscode('');
    setNewPasscode('');
    setConfirmPasscode('');
    setError('');
    setPassword('');
    setShake(false);
    setShowPasscode(false);
  }, [mode]);

  // Focus hidden input when entering passcode mode
  useEffect(() => {
    if (isPasscodeEntryMode) {
      setTimeout(() => hiddenInputRef.current?.focus(), 150);
    }
  }, [mode, isPasscodeEntryMode]);

  // Handle passcode input
  const handleInput = (e, setter, maxLength, onComplete) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, maxLength);
    setter(value);
    if (value.length === maxLength && onComplete) {
      setTimeout(() => onComplete(value), 150);
    }
  };

  const handleSetComplete = (value) => {
    savedNewPasscode.current = value;
    setMode('confirm');
  };

  const handleConfirmComplete = (value) => {
    if (value !== savedNewPasscode.current) {
      setError('Passcodes do not match');
      setShake(true);
      setTimeout(() => { setShake(false); setConfirmPasscode(''); hiddenInputRef.current?.focus(); }, 500);
      return;
    }
    setPasscode(value);
    togglePasscodeEnabled(true);
    setMode('menu');
  };

  const handleVerifyForAction = (value) => {
    if (verifyPasscode(value)) {
      if (verifyAction === 'disable') { togglePasscodeEnabled(false); setMode('menu'); }
      else if (verifyAction === 'remove') { clearPasscode(); setMode('menu'); }
      else { setMode('set'); }
    } else {
      setError('Incorrect passcode');
      setShake(true);
      setTimeout(() => { setShake(false); setCurrentPasscode(''); hiddenInputRef.current?.focus(); }, 500);
    }
  };

  const handleVerifyPassword = async () => {
    if (!password.trim()) { setError('Please enter your password'); return; }
    setVerifyingPassword(true);
    setError('');
    try {
      await authAPI.verifyPassword({ password });
      setMode('set');
    } catch (err) {
      setError('Incorrect password');
    } finally {
      setVerifyingPassword(false);
    }
  };

  const handleToggleEnabled = () => {
    if (isPasscodeEnabled) { setVerifyAction('disable'); setMode('verify-current'); }
    else if (isPasscodeSet) { togglePasscodeEnabled(true); }
    else { setMode('set'); }
  };

  // Render passcode dots with hidden input
  const renderPasscodeInput = (value, setter, length, onComplete) => (
    <div className="relative" onClick={() => hiddenInputRef.current?.focus()}>
      <input
        ref={hiddenInputRef}
        type="tel"
        inputMode="numeric"
        pattern="[0-9]*"
        value={value}
        onChange={(e) => handleInput(e, setter, length, onComplete)}
        className="absolute opacity-0 w-px h-px"
        style={{ left: '-9999px', top: '-9999px' }}
        autoComplete="off"
        autoFocus
      />
      <div className={`flex justify-center ${length === 6 ? 'gap-2.5' : 'gap-3'} py-6 ${shake ? 'animate-shake' : ''}`}>
        {Array.from({ length }).map((_, i) => (
          <div
            key={i}
            className={`w-11 h-11 rounded-full border-2 flex items-center justify-center transition-all ${
              i < value.length
                ? 'border-primary-500 bg-primary-50'
                : i === value.length
                ? 'border-primary-400 bg-transparent'
                : 'border-surface-200 bg-transparent'
            }`}
          >
            {i < value.length && (
              showPasscode
                ? <span className="text-lg font-bold text-primary-600">{value[i]}</span>
                : <div className="w-3 h-3 rounded-full bg-primary-500" />
            )}
          </div>
        ))}
      </div>
    </div>
  );

  // Show/hide toggle
  const renderShowHide = () => (
    <button
      onClick={() => setShowPasscode(!showPasscode)}
      className={`flex items-center justify-center gap-1.5 mb-2 w-full ${showPasscode ? 'text-primary-600' : 'text-surface-400'}`}
    >
      {showPasscode ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
      <span className="text-xs font-medium">{showPasscode ? 'Hide' : 'Show'}</span>
    </button>
  );

  // ===== MENU MODE =====
  const renderMenu = () => (
    <div className="space-y-3">
      <div className="bg-white rounded-2xl border border-surface-200 p-4">
        <div className="flex items-center gap-3 pb-3.5" style={{ borderBottom: '1px solid #f1f5f9' }}>
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ background: isPasscodeEnabled ? '#dcfce7' : '#fef9c3' }}
          >
            <Lock className="w-5 h-5" style={{ color: isPasscodeEnabled ? '#16a34a' : '#ca8a04' }} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[15px] font-bold text-surface-900">App Lock</p>
            <p className="text-[11px]" style={{ color: isPasscodeEnabled ? '#16a34a' : '#94a3b8' }}>
              {isPasscodeEnabled ? 'Enabled - locks when you leave' : 'Protect your financial data'}
            </p>
          </div>
          <button
            onClick={handleToggleEnabled}
            className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 ${
              isPasscodeEnabled ? 'bg-primary-500' : 'bg-surface-200'
            }`}
          >
            <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow-md transition-transform duration-200 ${
              isPasscodeEnabled ? 'translate-x-5' : 'translate-x-0'
            }`} />
          </button>
        </div>

        {isPasscodeSet && isPasscodeEnabled ? (
          <>
            <button
              onClick={() => { setVerifyAction('change'); setMode('verify-current'); }}
              className="flex items-center justify-between w-full py-3 text-left"
              style={{ borderBottom: '1px solid #f1f5f9' }}
            >
              <div>
                <p className="text-[13px] font-semibold text-surface-900">Change Passcode</p>
                <p className="text-[11px] text-surface-400 mt-0.5">Update your current PIN</p>
              </div>
              <ChevronRight className="w-4 h-4 text-surface-400" />
            </button>
            <div className="flex items-center justify-between py-3">
              <div>
                <p className="text-[13px] font-semibold text-surface-900">Passcode Type</p>
                <p className="text-[11px] text-surface-400 mt-0.5">Currently using</p>
              </div>
              <span className="text-xs font-semibold text-primary-600">{passcodeLength}-digit PIN</span>
            </div>
          </>
        ) : !isPasscodeSet && (
          <div className="flex items-start gap-2 mt-3.5 p-2.5 rounded-xl" style={{ background: '#f0fdf4', border: '1px solid #bbf7d0' }}>
            <span className="text-sm flex-shrink-0 mt-0.5">🛡️</span>
            <span className="text-[11px] leading-relaxed" style={{ color: '#166534' }}>
              Enable app lock to protect your financial data with a passcode. The app will lock when you switch away.
            </span>
          </div>
        )}
      </div>

      {isPasscodeSet && isPasscodeEnabled && (
        <>
          <button
            onClick={() => { setVerifyAction('remove'); setMode('verify-current'); }}
            className="w-full py-3 rounded-xl border flex items-center justify-center gap-2 text-[13px] font-semibold transition-colors"
            style={{ background: '#fef2f2', borderColor: '#fecaca', color: '#ef4444' }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/></svg>
            Remove Passcode
          </button>
          <div className="mt-2 space-y-1">
            <p className="text-[11px] text-surface-400">
              <span className="font-semibold text-surface-500">Toggle off</span> - temporarily disable, keeps your passcode
            </p>
            <p className="text-[11px] text-surface-400">
              <span className="font-semibold text-surface-500">Remove</span> - deletes passcode completely
            </p>
          </div>
        </>
      )}
    </div>
  );

  // ===== SET PASSCODE =====
  const renderSetPasscode = () => (
    <div className="flex flex-col items-center">
      <div className="w-16 h-16 rounded-[20px] flex items-center justify-center mb-4" style={{ background: '#f0fdf4' }}>
        <Lock className="w-7 h-7" style={{ color: '#2d6a4f' }} />
      </div>
      <h2 className="text-xl font-bold text-surface-900">{isPasscodeSet ? 'Set New Passcode' : 'Create Passcode'}</h2>
      <p className="text-[13px] text-surface-400 mt-1">Enter a {selectedLength}-digit passcode</p>

      {!isPasscodeSet && (
        <div className="flex rounded-[10px] p-0.5 gap-0.5 mt-5" style={{ background: '#f1f5f9' }}>
          <button
            onClick={() => { setSelectedLength(4); setNewPasscode(''); setTimeout(() => hiddenInputRef.current?.focus(), 100); }}
            className={`px-5 py-1.5 rounded-lg text-xs font-semibold transition-all ${
              selectedLength === 4 ? 'bg-white text-surface-900 shadow-sm' : 'text-surface-500'
            }`}
          >4 Digits</button>
          <button
            onClick={() => { setSelectedLength(6); setNewPasscode(''); setTimeout(() => hiddenInputRef.current?.focus(), 100); }}
            className={`px-5 py-1.5 rounded-lg text-xs font-semibold transition-all ${
              selectedLength === 6 ? 'bg-white text-surface-900 shadow-sm' : 'text-surface-500'
            }`}
          >6 Digits</button>
        </div>
      )}

      {renderPasscodeInput(newPasscode, setNewPasscode, selectedLength, handleSetComplete)}
      {renderShowHide()}
      {error && (
        <div className="flex items-center gap-2 mb-2 text-expense-600">
          <AlertCircle className="w-4 h-4" /><span className="text-sm">{error}</span>
        </div>
      )}
      <p className="text-xs text-surface-400 mt-2">Tap above to open keyboard</p>
    </div>
  );

  // ===== CONFIRM PASSCODE =====
  const renderConfirmPasscode = () => (
    <div className="flex flex-col items-center">
      <div className="w-16 h-16 rounded-[20px] flex items-center justify-center mb-4" style={{ background: '#f0fdf4' }}>
        <Lock className="w-7 h-7" style={{ color: '#2d6a4f' }} />
      </div>
      <h2 className="text-xl font-bold text-surface-900">Confirm Passcode</h2>
      <p className="text-[13px] text-surface-400 mt-1">Re-enter your passcode to confirm</p>

      {renderPasscodeInput(confirmPasscode, setConfirmPasscode, selectedLength, handleConfirmComplete)}
      {renderShowHide()}
      {error && (
        <div className="flex items-center gap-2 mb-2 text-expense-600">
          <AlertCircle className="w-4 h-4" /><span className="text-sm">{error}</span>
        </div>
      )}
    </div>
  );

  // ===== VERIFY CURRENT =====
  const renderVerifyCurrentPasscode = () => (
    <div className="flex flex-col items-center">
      <div className="w-16 h-16 rounded-[20px] flex items-center justify-center mb-4" style={{ background: '#f1f5f9' }}>
        <Lock className="w-7 h-7 text-surface-600" />
      </div>
      <h2 className="text-xl font-bold text-surface-900">Enter Current Passcode</h2>
      <p className="text-[13px] text-surface-400 mt-1">
        {verifyAction === 'disable' ? 'Verify to disable app lock'
          : verifyAction === 'remove' ? 'Verify to remove passcode'
          : 'Verify to change passcode'}
      </p>

      {renderPasscodeInput(currentPasscode, setCurrentPasscode, passcodeLength, handleVerifyForAction)}
      {renderShowHide()}
      {error && (
        <div className="flex items-center gap-2 mb-2 text-expense-600">
          <AlertCircle className="w-4 h-4" /><span className="text-sm">{error}</span>
        </div>
      )}
      <button onClick={() => setMode('verify-password')} className="mt-2 text-sm text-primary-600 font-medium">
        Forgot passcode? Use password
      </button>
    </div>
  );

  // ===== VERIFY PASSWORD =====
  const renderVerifyPassword = () => (
    <div className="space-y-6">
      <button onClick={() => setMode('menu')} className="flex items-center gap-2 text-surface-600 hover:text-surface-900 transition-colors">
        <ArrowLeft className="w-5 h-5" /><span className="text-sm font-medium">Back</span>
      </button>
      <div className="text-center">
        <div className="w-16 h-16 rounded-2xl bg-expense-100 flex items-center justify-center mx-auto mb-4">
          <KeyRound className="w-8 h-8 text-expense-600" />
        </div>
        <h2 className="text-xl font-semibold text-surface-900">Reset with Password</h2>
        <p className="text-surface-500 mt-1">Enter your account password to reset passcode</p>
      </div>
      <div className="max-w-xs mx-auto space-y-4">
        <div>
          <label className="block text-sm font-medium text-surface-700 mb-1.5">Account Password</label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Enter your password"
            className="w-full px-4 py-3 bg-white border border-surface-200 rounded-xl text-surface-900 placeholder-surface-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
            autoFocus
          />
        </div>
        {error && (
          <div className="flex items-center gap-2 text-expense-600">
            <AlertCircle className="w-4 h-4" /><span className="text-sm">{error}</span>
          </div>
        )}
        <button
          onClick={handleVerifyPassword}
          disabled={!password.trim() || verifyingPassword}
          className="w-full py-3 rounded-xl font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          style={{ backgroundColor: 'var(--accent-bg)', color: 'var(--accent-text)' }}
        >
          {verifyingPassword ? (<><Loader2 className="w-5 h-5 animate-spin" />Verifying...</>) : 'Reset Passcode'}
        </button>
      </div>
    </div>
  );

  // Back button for entry screens
  const renderBackButton = () => (
    <button
      onClick={() => setMode(mode === 'confirm' ? 'set' : 'menu')}
      className="flex items-center gap-1 text-primary-600 font-semibold text-[13px] mb-3"
    >
      <ArrowLeft className="w-4 h-4" />
      {mode === 'confirm' ? 'Back' : 'Security'}
    </button>
  );

  return (
    <div className="space-y-4">
      {!isPasscodeEntryMode && (
        <>
          <div className="xl:hidden">
            <h1 className="text-lg font-semibold text-surface-900">Passcode Settings</h1>
            <p className="text-xs text-surface-500">Secure your app</p>
          </div>
          <div className="hidden xl:block page-header">
            <h1 className="page-title">Passcode Settings</h1>
            <p className="page-subtitle">Secure your app with a passcode</p>
          </div>
        </>
      )}

      {mode === 'menu' && renderMenu()}

      {isPasscodeEntryMode && (
        <div>
          {renderBackButton()}
          {mode === 'set' && renderSetPasscode()}
          {mode === 'confirm' && renderConfirmPasscode()}
          {mode === 'verify-current' && renderVerifyCurrentPasscode()}
        </div>
      )}

      {mode === 'verify-password' && (
        <div className="bg-white rounded-2xl border border-surface-200 p-6">
          {renderVerifyPassword()}
        </div>
      )}

      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-10px); }
          75% { transform: translateX(10px); }
        }
        .animate-shake { animation: shake 0.3s ease-in-out; }
      `}</style>
    </div>
  );
}
